import { getToken, refreshToken } from './token-store'

const DEV_GRAPHQL_URL = 'https://hikari-github-tasamago-rz-mac.taila7050b.ts.net/graphql'

interface ResolveGraphqlUrlOptions {
  configuredUrl: string | undefined
  isDev: boolean
}

export function resolveGraphqlUrl({
  configuredUrl,
  isDev,
}: ResolveGraphqlUrlOptions): string {
  const url = configuredUrl?.trim()
  if (url) {
    return url
  }
  if (isDev) {
    return DEV_GRAPHQL_URL
  }
  throw new Error('VITE_GRAPHQL_URL is required outside development')
}

const GRAPHQL_URL = resolveGraphqlUrl({
  configuredUrl: import.meta.env.VITE_GRAPHQL_URL,
  isDev: import.meta.env.DEV,
})

export interface GraphQLError {
  message: string
  path?: string[]
  extensions?: { code?: string }
}

export interface GraphQLResponse<T> {
  data?: T
  errors?: GraphQLError[]
}

// Check if response contains an UNAUTHENTICATED error
function hasAuthError(result: GraphQLResponse<unknown>): boolean {
  return result.errors?.some(e => e.extensions?.code === 'UNAUTHENTICATED') ?? false
}

async function doRequest<T>(
  query: string,
  variables: Record<string, unknown> | undefined,
  token: string | undefined
): Promise<{ response: Response; result: GraphQLResponse<T> }> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(GRAPHQL_URL, {
    method: 'POST',
    headers,
    body: JSON.stringify({ query, variables }),
  })

  if (!response.ok) {
    throw new Error(`GraphQL request failed: ${response.statusText}`)
  }

  const result = await response.json() as GraphQLResponse<T>
  return { response, result }
}

export async function graphqlRequest<T>(
  query: string,
  variables?: Record<string, unknown>
): Promise<GraphQLResponse<T>> {
  let token = getToken()

  const initial = await doRequest<T>(query, variables, token)
  const { response } = initial
  let { result } = initial

  // If unauthorized (401 or UNAUTHENTICATED error), try to refresh and retry once
  if ((response.status === 401 || hasAuthError(result)) && token) {
    const refreshed = await refreshToken()
    if (refreshed) {
      token = getToken()
      if (token) {
        const retry = await doRequest<T>(query, variables, token)
        result = retry.result
      }
    }
  }

  return result
}
